import { AnimatePresence, motion } from "framer-motion";
import { string, bool, func, array } from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

import ArrowDownIcon from "../../shared/Icons/ArrowDown";
import { useTranslation, Trans, i18n } from "@lib/i18n";
import { withTabNavigation } from "@hoc/withTabNavigation";

import { scrollInFullView } from "@lib/scrollToRef";
import QuestionList from "./QuestionList";
import Icon from "@components/shared/Icons/Icon";
import Typography from "@components/shared/Typography";

const CollapsedContent = withTabNavigation("div");

const FooterFAQ = ({
  content,
  isOpen,
  label,
  onClick,
  isLast,
  description,
}) => {
  const { t } = useTranslation();
  const answerRef = useRef(null);
  const [isSelected, setIsSelected] = useState("");

  const handleSelection = label =>
    setIsSelected(isSelected === label ? "" : label);

  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      if (isOpen) {
        scrollInFullView(answerRef);
      }
    }, 100);
    return () => {
      clearTimeout(scrollTimeout);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsSelected("");
    }
  }, [isOpen]);

  return (
    <div
      className={twMerge(
        "px-4 my-2 transition-colors duration-300",
        isOpen
          ? "bg-primaryLight text-primaryDark pb-4"
          : "bg-primaryDark text-white"
      )}
    >
      <CollapsedContent
        className={twMerge(
          "flex justify-between items-center max-w-pageContent py-6 mx-auto",
          "cursor-pointer transition-colors duration-100 ease-linear",
          !(isOpen || isLast) && "border-b-2 border-b-darkGrey",
          isOpen
            ? "text-black hover:text-primaryDark"
            : "text-white hover:text-white/80"
        )}
        onClick={() => onClick(label)}
      >
        <h3 className="text-xl/normal font-normal">
          <Trans data-id={`section-${label}`} i18nKey={label} />
        </h3>
        <Icon
          className={twMerge(
            "transition-all duration-300 ease-linear mr-1 size-3",
            isOpen && "-scale-y-100"
          )}
          data-id={`question-${label}-icon-${isOpen ? "close" : "open"}`}
          icon={<ArrowDownIcon height="12px" width="12px" />}
        />
      </CollapsedContent>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="max-w-pageContent mx-auto overflow-hidden"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: {
                opacity: 1,
                height: "auto",
                display: "block",
              },
              collapsed: {
                opacity: 0,
                height: 0,
                transitionEnd: { display: "none" },
                transition: { duration: 0.3, type: "tween" },
              },
            }}
            ref={answerRef}
          >
            <Typography
              className="mb-4 text-lg/normal text-black"
              data-id={`section-description-${label}`}
            >
              <Trans i18nKey={description} />
            </Typography>
            {content
              .filter(
                section =>
                  i18n.exists(`footer.faq.sections.${section}.title`) &&
                  t(`footer.faq.sections.${section}.title`)
              )
              .map((section, sectionIndex) => {
                const title = t(`footer.faq.sections.${section}.title`);
                const questions = t(
                  `footer.faq.sections.${section}.questions`,
                  {
                    returnObjects: true,
                  }
                );

                return (
                  <div className="text-black mb-4" key={sectionIndex}>
                    <CollapsedContent
                      className={twMerge(
                        "flex justify-between items-center max-w-pageContent px-6 py-4 mx-auto",
                        "cursor-pointer transition-colors duration-100 ease-linear",
                        "border border-grey bg-white rounded-lg",
                        "hover:text-black hover:bg-lightGrey"
                      )}
                      onClick={() => handleSelection(title)}
                    >
                      <h4 className="text-black text-lg/normal uppercase">
                        <Trans i18nKey={title} />
                      </h4>
                      <Icon
                        className={twMerge(
                          "transition-all duration-300 ease-linear mr-1 text-grey size-3",
                          isSelected === title && "-scale-y-100"
                        )}
                        data-id={`question-${title}-icon-${
                          isSelected ? "close" : "open"
                        }`}
                        icon={<ArrowDownIcon size="sm" />}
                      />
                    </CollapsedContent>
                    <AnimatePresence>
                      {isSelected === title && (
                        <motion.div
                          className="max-w-pageContent mx-auto px-4 overflow-hidden"
                          key={sectionIndex}
                          initial="collapsed"
                          animate="open"
                          exit="collapsed"
                          variants={{
                            open: {
                              opacity: 1,
                              height: "auto",
                              display: "block",
                            },
                            collapsed: {
                              opacity: 0,
                              height: 0,
                              transitionEnd: { display: "none" },
                              transition: { duration: 0.3, type: "tween" },
                            },
                          }}
                        >
                          <QuestionList
                            questions={questions}
                            title={title}
                            section={section}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

FooterFAQ.propTypes = {
  isOpen: bool,
  label: string.isRequired,
  onClick: func.isRequired,
  isLast: bool,
  content: array,
  description: string,
};

export default FooterFAQ;
